import { AxiosError, AxiosResponse } from "axios";
import { useCallback, useMemo, useState } from "react";
import { DEFAULT_OPTIONS } from "service/HttpService";
import NotificationPopUp from "util/NotificationPopUp";
import Utils from "util/Utils";
import {
  OpcUaCertificateDTO,
  OpcUaX509UserRequest,
} from "../../../serviceapi/dataCollectorApi";
import {
  OpcUaCertificateKind,
  certificateLabel,
  reportBlobRequestError,
  showBulkResult,
  toSafeFileName,
} from "../opcUaUtils";
import { useOpcUaApi } from "./useOpcUaApi";

const TRUST_CONFLICT_MESSAGE =
  "This certificate is registered as a user or issuer certificate and cannot be trusted as an application certificate.";

/**
 * Certificate and X.509 identity management (/rest/opcua/certificates and
 * /rest/opcua/users/x509). One GET of the full certificate list feeds both the X.509 users
 * tab and the application certificates tab, so they never fetch twice.
 */
export const useOpcUaCertificates = () => {
  const { opcUaApi } = useOpcUaApi();

  const [certificates, setCertificates] = useState<OpcUaCertificateDTO[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);

  const fetchCertificates = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await opcUaApi.getOpcUaCertificates(undefined, DEFAULT_OPTIONS);
      setCertificates(response.data ?? []);
    } catch (error) {
      NotificationPopUp.handleGenericError(
        error,
        "Failed to fetch OPC UA certificates."
      );
    } finally {
      setIsLoading(false);
    }
  }, [opcUaApi]);

  const x509Users = useMemo(
    () => certificates.filter((c) => c.kind === OpcUaCertificateKind.USER),
    [certificates]
  );

  const applicationCertificates = useMemo(
    () => certificates.filter((c) => c.kind !== OpcUaCertificateKind.USER),
    [certificates]
  );

  const pendingCount = useMemo(
    () =>
      certificates.filter((c) => c.kind === OpcUaCertificateKind.REJECTED).length,
    [certificates]
  );

  // --- raw operations, no toasts: shared by the single-row and bulk paths -----------------

  const trustRaw = useCallback(
    (thumbprint: string) =>
      opcUaApi.trustOpcUaCertificate(thumbprint, DEFAULT_OPTIONS),
    [opcUaApi]
  );

  const revokeRaw = useCallback(
    (thumbprint: string) =>
      opcUaApi.revokeOpcUaCertificate(thumbprint, DEFAULT_OPTIONS),
    [opcUaApi]
  );

  const deleteRaw = useCallback(
    (thumbprint: string) =>
      opcUaApi.deleteOpcUaCertificate(thumbprint, DEFAULT_OPTIONS),
    [opcUaApi]
  );

  // --- single-row operations --------------------------------------------------------------

  /**
   * Trust is the only way back for a revoked certificate — the API has no un-revoke — so
   * both "approve" and "unban" hit this endpoint and differ only in wording.
   */
  const applyTrust = useCallback(
    async (
      certificate: OpcUaCertificateDTO,
      pastTense: string,
      infinitive: string
    ) => {
      const thumbprint = certificate.thumbprint;
      if (!thumbprint) return;

      try {
        setIsBusy(true);
        await trustRaw(thumbprint);
        NotificationPopUp.show(
          `${pastTense} "${certificateLabel(certificate)}".`,
          "success"
        );
      } catch (error) {
        // The spec documents only a 409 for this endpoint; call it out specifically.
        if (error instanceof AxiosError && error.response?.status === 409) {
          NotificationPopUp.show(
            TRUST_CONFLICT_MESSAGE,
            "warn",
            `Cannot ${infinitive}`
          );
        } else {
          NotificationPopUp.handleGenericError(
            error,
            `Failed to ${infinitive} "${certificateLabel(certificate)}".`
          );
        }
      } finally {
        setIsBusy(false);
        await fetchCertificates();
      }
    },
    [trustRaw, fetchCertificates]
  );

  const trustCertificate = useCallback(
    (certificate: OpcUaCertificateDTO) =>
      applyTrust(certificate, "Approved", "approve"),
    [applyTrust]
  );

  const unbanCertificate = useCallback(
    (certificate: OpcUaCertificateDTO) =>
      applyTrust(certificate, "Unbanned", "unban"),
    [applyTrust]
  );

  const revokeCertificate = useCallback(
    async (certificate: OpcUaCertificateDTO) => {
      const thumbprint = certificate.thumbprint;
      if (!thumbprint) return;

      try {
        setIsBusy(true);
        await revokeRaw(thumbprint);
        NotificationPopUp.show(
          `Banned "${certificateLabel(certificate)}".`,
          "success"
        );
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          `Failed to ban "${certificateLabel(certificate)}".`
        );
      } finally {
        setIsBusy(false);
        await fetchCertificates();
      }
    },
    [revokeRaw, fetchCertificates]
  );

  const deleteCertificate = useCallback(
    async (certificate: OpcUaCertificateDTO) => {
      const thumbprint = certificate.thumbprint;
      if (!thumbprint) return;

      try {
        setIsBusy(true);
        await deleteRaw(thumbprint);
        NotificationPopUp.show(
          `Deleted "${certificateLabel(certificate)}".`,
          "success"
        );
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          `Failed to delete "${certificateLabel(certificate)}".`
        );
      } finally {
        setIsBusy(false);
        await fetchCertificates();
      }
    },
    [deleteRaw, fetchCertificates]
  );

  const downloadCertificate = useCallback(
    async (certificate: OpcUaCertificateDTO) => {
      const thumbprint = certificate.thumbprint;
      if (!thumbprint) return;

      try {
        // The spec gives this response an empty schema, so the generated method is typed
        // void; ask axios for a blob and override the client's default JSON Accept header.
        const response = (await opcUaApi.downloadOpcUaCertificate(thumbprint, {
          ...DEFAULT_OPTIONS,
          responseType: "blob",
          headers: { Accept: "application/octet-stream" },
        })) as unknown as AxiosResponse<Blob>;

        const fileName = toSafeFileName(certificateLabel(certificate), thumbprint);
        Utils.downloadFile(new File([response.data], `${fileName}.der`));
      } catch (error) {
        // Blob response type, so the error body needs unwrapping before it can be reported.
        await reportBlobRequestError(
          error,
          `Failed to download "${certificateLabel(certificate)}".`
        );
      }
    },
    [opcUaApi]
  );

  const uploadTrustedCertificate = useCallback(
    async (file: File) => {
      try {
        setIsBusy(true);
        // Endpoint takes the raw file as an octet-stream body, not multipart FormData.
        const response = await opcUaApi.addTrustedOpcUaCertificate(
          file,
          DEFAULT_OPTIONS
        );
        NotificationPopUp.show(
          `Trusted "${certificateLabel(response.data ?? {})}".`,
          "success"
        );
        return true;
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          "Failed to upload trusted certificate."
        );
        return false;
      } finally {
        setIsBusy(false);
        await fetchCertificates();
      }
    },
    [opcUaApi, fetchCertificates]
  );

  // --- X.509 identities ------------------------------------------------------------------

  /**
   * Issues a new X.509 identity. The response is a zip holding the certificate and the
   * private key — the key is not retrievable afterwards, so the download must not be lost.
   */
  const createX509User = useCallback(
    async (request: OpcUaX509UserRequest) => {
      const username = request.username ?? "user";

      try {
        setIsBusy(true);
        const response = (await opcUaApi.createOpcUaX509User(request, {
          ...DEFAULT_OPTIONS,
          responseType: "blob",
          headers: { Accept: "application/zip" },
        })) as unknown as AxiosResponse<Blob>;

        Utils.downloadFile(
          new File(
            [response.data],
            `${toSafeFileName(username, "x509_user")}_x509.zip`
          )
        );
        NotificationPopUp.show(
          `Issued X.509 user "${username}". The private key is only in the downloaded zip.`,
          "success"
        );
        return true;
      } catch (error) {
        // Blob response type, so the error body needs unwrapping before it can be reported.
        await reportBlobRequestError(
          error,
          `Failed to issue X.509 user "${username}".`
        );
        return false;
      } finally {
        setIsBusy(false);
        await fetchCertificates();
      }
    },
    [opcUaApi, fetchCertificates]
  );

  const importX509User = useCallback(
    async (file: File, username?: string) => {
      try {
        setIsBusy(true);
        const response = await opcUaApi.importOpcUaX509User(
          file,
          username,
          DEFAULT_OPTIONS
        );
        NotificationPopUp.show(
          `Imported X.509 user "${certificateLabel(response.data ?? {})}".`,
          "success"
        );
        return true;
      } catch (error) {
        NotificationPopUp.handleGenericError(
          error,
          "Failed to import X.509 certificate."
        );
        return false;
      } finally {
        setIsBusy(false);
        await fetchCertificates();
      }
    },
    [opcUaApi, fetchCertificates]
  );

  // --- bulk operations -------------------------------------------------------------------

  /** Runs sequentially so one failure never aborts the rest; reports a single summary. */
  const runBulk = useCallback(
    async (
      selected: OpcUaCertificateDTO[],
      verb: string,
      operation: (thumbprint: string) => Promise<unknown>
    ) => {
      const targets = selected.filter((c) => !!c.thumbprint);
      if (targets.length === 0) return;

      let succeeded = 0;
      setIsBusy(true);

      for (const certificate of targets) {
        try {
          await operation(certificate.thumbprint as string);
          succeeded += 1;
        } catch (error) {
          console.error(`Bulk ${verb} failed for ${certificate.thumbprint}`, error);
        }
      }

      setIsBusy(false);
      showBulkResult(verb, succeeded, targets.length);
      await fetchCertificates();
    },
    [fetchCertificates]
  );

  const bulkTrust = useCallback(
    (selected: OpcUaCertificateDTO[]) => runBulk(selected, "Approved", trustRaw),
    [runBulk, trustRaw]
  );

  const bulkRevoke = useCallback(
    (selected: OpcUaCertificateDTO[]) => runBulk(selected, "Banned", revokeRaw),
    [runBulk, revokeRaw]
  );

  const bulkDelete = useCallback(
    (selected: OpcUaCertificateDTO[]) => runBulk(selected, "Deleted", deleteRaw),
    [runBulk, deleteRaw]
  );

  return {
    x509Users,
    applicationCertificates,
    pendingCount,
    isLoading,
    isBusy,
    fetchCertificates,
    trustCertificate,
    unbanCertificate,
    revokeCertificate,
    deleteCertificate,
    downloadCertificate,
    uploadTrustedCertificate,
    createX509User,
    importX509User,
    bulkTrust,
    bulkRevoke,
    bulkDelete,
  };
};
